/* eslint-disable */
import React, { useRef, useState, useCallback } from 'react';
import './index.less';
import './index.en.less';

export interface INumberedStepItem {
  text: string;
  backgroundImage?: string;
}

export interface INumberedStepsIllustrationProps {
  mainTitle?: string;
  description?: string;
  illustration?: React.ReactNode;
  items?: INumberedStepItem[];
  onComplete?: () => void;
}

const NumberedStepsIllustration: React.FunctionComponent<INumberedStepsIllustrationProps> = ({
  mainTitle,
  description,
  illustration,
  items = [],
  onComplete,
}) => {
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [isDragging, setIsDragging] = useState(false);
  const dragStartX = useRef(0);
  const scrollLeft = useRef(0);
  const hasMoved = useRef(false);
  const completedRef = useRef(false);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  const updateScrollState = useCallback(() => {
    const el = scrollContainerRef.current;
    if (!el) return;
    setCanScrollLeft(el.scrollLeft > 1);
    setCanScrollRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 1);
  }, []);

  React.useEffect(() => {
    const el = scrollContainerRef.current;
    if (!el) return;
    updateScrollState();
    el.addEventListener('scroll', updateScrollState, { passive: true });
    window.addEventListener('resize', updateScrollState);
    return () => {
      el.removeEventListener('scroll', updateScrollState);
      window.removeEventListener('resize', updateScrollState);
    };
  }, [updateScrollState, items.length]);

  const scrollByDirection = (dir: 1 | -1) => {
    const el = scrollContainerRef.current;
    if (!el) return;
    el.scrollBy({ left: dir * el.clientWidth * 0.8, behavior: 'smooth' });
    if (!completedRef.current) {
      completedRef.current = true;
      onComplete?.();
    }
  };

  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    if (!scrollContainerRef.current) return;
    setIsDragging(true);
    dragStartX.current = e.pageX - scrollContainerRef.current.offsetLeft;
    scrollLeft.current = scrollContainerRef.current.scrollLeft;
    hasMoved.current = false;
  }, []);

  const handleMouseMove = useCallback((e: React.MouseEvent) => {
    if (!isDragging || !scrollContainerRef.current) return;
    e.preventDefault();
    const x = e.pageX - scrollContainerRef.current.offsetLeft;
    const walk = (x - dragStartX.current) * 1.5;
    scrollContainerRef.current.scrollLeft = scrollLeft.current - walk;
    if (Math.abs(walk) > 5) {
      hasMoved.current = true;
    }
  }, [isDragging]);

  const handleMouseUp = useCallback(() => {
    setIsDragging(false);
    if (!completedRef.current && hasMoved.current) {
      completedRef.current = true;
      onComplete?.();
    }
  }, [onComplete]);

  const handleMouseLeave = useCallback(() => {
    if (isDragging) {
      setIsDragging(false);
    }
  }, [isDragging]);

  const handleTouchStart = useCallback((e: React.TouchEvent) => {
    if (!scrollContainerRef.current) return;
    dragStartX.current = e.touches[0].pageX - scrollContainerRef.current.offsetLeft;
    scrollLeft.current = scrollContainerRef.current.scrollLeft;
    hasMoved.current = false;
  }, []);

  const handleTouchMove = useCallback((e: React.TouchEvent) => {
    if (!scrollContainerRef.current) return;
    const x = e.touches[0].pageX - scrollContainerRef.current.offsetLeft;
    const walk = (x - dragStartX.current) * 1.5;
    scrollContainerRef.current.scrollLeft = scrollLeft.current - walk;
    if (Math.abs(walk) > 5) {
      hasMoved.current = true;
    }
  }, []);

  const handleTouchEnd = useCallback(() => {
    if (!completedRef.current && hasMoved.current) {
      completedRef.current = true;
      onComplete?.();
    }
  }, [onComplete]);

  return (
    <div className="numbered-steps-illustration">
      <div className="nsi-header">
        {mainTitle && <h2 className="nsi-title">{mainTitle}</h2>}
        {description && <p className="nsi-description">{description}</p>}
      </div>
      <div className="nsi-scroll-area">
      <div
        ref={scrollContainerRef}
        className={`nsi-cards-scroll${isDragging ? ' is-dragging' : ''}`}
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseLeave}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
      >
        <div className="nsi-cards-container">
          {items.map((item, index) => {
            const bgImage = item.backgroundImage;
            const cardStyle = bgImage
              ? ({ '--nsi-card-bg': `url(${bgImage})` } as React.CSSProperties)
              : undefined;
            return (
              <div
                key={index}
                className="nsi-card"
                style={cardStyle}
              >
                <div className="nsi-card-number">
                  {String(index + 1).padStart(2, '0')}
                </div>
                <div className="nsi-card-content">
                  <p className="nsi-card-text">{item.text}</p>
                </div>
              </div>
            );
          })}
        </div>
      </div>
      {canScrollLeft && (
        <button
          type="button"
          className="nsi-scroll-btn nsi-scroll-btn-left"
          aria-label="Scroll left"
          onClick={() => scrollByDirection(-1)}
        >
          <svg
            viewBox="0 0 20 20"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M12.5 15L7.5 10L12.5 5"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </button>
      )}
      {canScrollRight && (
        <button
          type="button"
          className={`nsi-scroll-btn nsi-scroll-btn-right${
            !canScrollLeft ? ' nsi-scroll-hint' : ''
          }`}
          aria-label="Scroll right"
          onClick={() => scrollByDirection(1)}
        >
          <svg
            viewBox="0 0 20 20"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M7.5 15L12.5 10L7.5 5"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </button>
      )}
      </div>
      {illustration && (
        <div className="nsi-illustration">
          {illustration}
        </div>
      )}
    </div>
  );
};

export default React.memo(NumberedStepsIllustration);
